import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Info, Pencil, Trash2 } from 'lucide-react';
import moment from 'moment';

import { Version } from '../../../types/project';
import {
  isAutopositioningInProgress as checkIsAutopositioningInProgress,
  isCalculationInProgress as checkIsCalculationInProgress,
  isBakingJobCompleted,
  isBakingJobFailed,
  isJobFailed,
  isRenderBakingInProgress,
} from '../../../utils/jobStatus';

interface VersionHistoryListProps {
  versions: Version[];
  activeVersionId: number;
  onVersionChange: (versionId: number) => void;
  onDeleteVersion: (versionId: number) => void;
  onRenameVersion: (versionId: number, newName: string) => void;
  onCloseAutoPosition?: () => void;
}

type VersionState =
  | 'ready'
  | 'not-ready'
  | 'calculation-in-progress'
  | 'baking-in-progress'
  | 'calculation-failed'
  | 'baking-failed'
  | 'completed';

interface VersionProgress {
  state: VersionState;
  percentage?: number;
}

interface VersionStats {
  chlCount: number;
  lmnCount: number;
}

/**
 * Switch-and-manage list rendered inside the navbar version pill's dropdown.
 *
 * The two write actions that create new versions ("new blank" and "duplicate
 * current") live in the navbar split-button block next to the pill, NOT in
 * this list — keeping them here would mean two entry points for the same
 * action. Per-row controls are pure metadata: rename + delete on the right
 * end of each row, always visible (used to be hover-revealed which made
 * users unsure if they were interactable).
 */
export const VersionHistoryList: React.FC<VersionHistoryListProps> = ({
  versions,
  activeVersionId,
  onVersionChange,
  onDeleteVersion,
  onRenameVersion,
  onCloseAutoPosition,
}) => {
  const { t } = useTranslation();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState<number | null>(null);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [versionToRename, setVersionToRename] = useState<number | null>(null);
  const [newVersionName, setNewVersionName] = useState('');
  // Inline-edit state for double-click rename. The pencil-button → modal
  // flow is preserved as the explicit / discoverable path; this one is
  // the power-user shortcut that mirrors the navbar project-name behavior.
  const [inlineEditId, setInlineEditId] = useState<number | null>(null);
  const [inlineEditName, setInlineEditName] = useState('');

  const getLastUpdatedTime = (version: Version): string => {
    const dates: Date[] = [];
    dates.push(new Date(version.updatedAt));

    if (version.calculationJob?.updatedAt) {
      dates.push(new Date(version.calculationJob.updatedAt));
    }
    if (version.autopositioningJob?.updatedAt) {
      dates.push(new Date(version.autopositioningJob.updatedAt));
    }
    if (version.renderBakingJob?.updatedAt) {
      dates.push(new Date(version.renderBakingJob.updatedAt));
    }

    if (dates.length === 0) {
      return t('workspace.lastUpdateNone');
    }

    const mostRecent = new Date(Math.max(...dates.map((d) => d.getTime())));
    return t('workspace.lastUpdate', { time: moment(mostRecent).fromNow() });
  };

  const getVersionStats = (version: Version): VersionStats => {
    const meshes = version.meshes || [];
    const chlCount = meshes.filter((mesh) => mesh.canHoldLight).length;
    const lmnCount = meshes.filter((mesh) => mesh.normId !== null && mesh.normId !== undefined).length;
    return { chlCount, lmnCount };
  };

  const getVersionProgress = (version: Version): VersionProgress => {
    if (checkIsCalculationInProgress(version) || checkIsAutopositioningInProgress(version)) {
      return { state: 'calculation-in-progress', percentage: 0 };
    }
    if (isRenderBakingInProgress(version)) {
      return { state: 'baking-in-progress', percentage: 0 };
    }

    if (isJobFailed(version.calculationJob) || isJobFailed(version.autopositioningJob)) {
      return { state: 'calculation-failed' };
    }
    if (isBakingJobFailed(version)) {
      return { state: 'baking-failed' };
    }

    if (isBakingJobCompleted(version)) {
      return { state: 'completed' };
    }

    const stats = getVersionStats(version);
    const hasLamps = (version.lights || []).length > 0;
    if ((hasLamps && stats.lmnCount > 0) || (stats.chlCount > 0 && stats.lmnCount > 0)) {
      return { state: 'ready' };
    }

    return { state: 'not-ready' };
  };

  const handleVersionClick = (versionId: number) => {
    if (versionId !== activeVersionId) {
      onCloseAutoPosition?.();
      onVersionChange(versionId);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, versionId: number) => {
    e.stopPropagation();
    setVersionToDelete(versionId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (versionToDelete !== null) {
      onDeleteVersion(versionToDelete);
      setDeleteConfirmOpen(false);
      setVersionToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setVersionToDelete(null);
  };

  const handleRenameClick = (e: React.MouseEvent, versionId: number) => {
    e.stopPropagation();
    const version = versions.find((v) => v.id === versionId);
    if (version) {
      setVersionToRename(versionId);
      setNewVersionName(version.name);
      setRenameModalOpen(true);
    }
  };

  const handleRenameConfirm = () => {
    if (versionToRename !== null && newVersionName.trim()) {
      onRenameVersion(versionToRename, newVersionName.trim());
      setRenameModalOpen(false);
      setVersionToRename(null);
      setNewVersionName('');
    }
  };

  const handleRenameCancel = () => {
    setRenameModalOpen(false);
    setVersionToRename(null);
    setNewVersionName('');
  };

  const startInlineEdit = (e: React.MouseEvent, version: Version) => {
    e.stopPropagation();
    setInlineEditId(version.id);
    setInlineEditName(version.name);
  };

  const commitInlineEdit = () => {
    if (inlineEditId !== null && inlineEditName.trim()) {
      onRenameVersion(inlineEditId, inlineEditName.trim());
    }
    setInlineEditId(null);
    setInlineEditName('');
  };

  const cancelInlineEdit = () => {
    setInlineEditId(null);
    setInlineEditName('');
  };

  const renderVersionStatus = (progress: VersionProgress) => {
    const showInfoIcon = ['ready', 'not-ready', 'calculation-failed', 'baking-failed', 'completed'].includes(
      progress.state,
    );

    const isInProgress = progress.state === 'calculation-in-progress' || progress.state === 'baking-in-progress';

    return (
      <div className="flex items-center gap-1.5 text-gray-300 w-full">
        <div className="w-[18px] shrink-0 flex items-center justify-center">{showInfoIcon && <Info size={18} />}</div>
        <span className={`text-sm text-left break-words ${isInProgress ? 'text-lime-400 font-medium' : ''}`}>
          {progress.state === 'ready' && t('workspace.readyToOptimize')}
          {progress.state === 'not-ready' && t('workspace.notReadyToOptimize')}
          {progress.state === 'calculation-failed' && t('workspace.optimizationFailed')}
          {progress.state === 'baking-failed' && t('workspace.bakingFailed')}
          {progress.state === 'completed' && t('workspace.completed').toLowerCase()}
          {progress.state === 'calculation-in-progress' && t('workspace.calculationInProgress')}
          {progress.state === 'baking-in-progress' && t('workspace.renderBakingInProgress')}
        </span>
      </div>
    );
  };

  return (
    <div className="bg-[#1c1c1c] rounded-lg overflow-hidden max-h-[50vh] overflow-y-auto shadow-2xl border border-white/5">
      <div className="divide-y divide-[#2a2a2a]">
        {versions.map((version) => {
          const isActive = activeVersionId === version.id;
          const stats = getVersionStats(version);
          const progress = getVersionProgress(version);

          return (
            <div
              key={version.id}
              onClick={() => handleVersionClick(version.id)}
              className={`group px-4 py-3 cursor-pointer transition-colors hover:bg-[#2d2d2d]`}
            >
              <div className="flex items-center justify-between gap-3">
                {/* Name + last updated. `flex-auto` (= flex 1 1 auto) means
                    the section starts at *content* width and only shrinks
                    when the dropdown hits its max-w cap — that's when
                    `truncate` actually clips the text. Short/medium names
                    display in full and the dropdown sizes to fit. */}
                <div className="flex-auto min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    {inlineEditId === version.id ? (
                      <input
                        type="text"
                        value={inlineEditName}
                        onChange={(e) => setInlineEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitInlineEdit();
                          else if (e.key === 'Escape') cancelInlineEdit();
                        }}
                        onBlur={commitInlineEdit}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                        className="text-sm font-medium text-white bg-black border border-lime-500 rounded px-2 py-0.5 focus:outline-none w-[280px] max-w-full"
                      />
                    ) : (
                      (() => {
                        // Same treatment as the "Updated …" line below: keep
                        // names on a single line, but only ellipsize once the
                        // string actually gets long. Short/medium names
                        // display in full and the dropdown grows to fit.
                        const NAME_TRUNCATE_AT = 45;
                        const shouldTruncate = version.name.length > NAME_TRUNCATE_AT;
                        return (
                          <span
                            className={`text-sm font-medium whitespace-nowrap min-w-0 ${
                              shouldTruncate ? 'truncate' : ''
                            } ${isActive ? 'text-lime-400' : 'text-white'}`}
                            onDoubleClick={(e) => startInlineEdit(e, version)}
                            title={shouldTruncate ? version.name : undefined}
                          >
                            {version.name}
                          </span>
                        );
                      })()
                    )}
                  </div>
                  {(() => {
                    // Keep the "Updated …" line on one full line when it's
                    // short (most cases: "Updated a few seconds ago"); only
                    // truncate it when the localised string actually gets
                    // long, so short names don't end up with "Updated a few
                    // seco…" because the name column was content-sized.
                    const lastUpdated = getLastUpdatedTime(version);
                    const SHOULD_TRUNCATE_AT = 35;
                    return (
                      <p
                        className={`text-xs text-gray-300 mt-0.5 whitespace-nowrap ${
                          lastUpdated.length > SHOULD_TRUNCATE_AT ? 'truncate' : ''
                        }`}
                        title={lastUpdated.length > SHOULD_TRUNCATE_AT ? lastUpdated : undefined}
                      >
                        {lastUpdated}
                      </p>
                    );
                  })()}
                </div>

                {/* Active dot + stats cluster. Dot lives on the left of the
                    CHL/LMN block (not on the title row) so the active marker
                    stays close to the row metadata while keeping the name
                    column visually clean. */}
                <div className="flex items-center gap-3 shrink-0 ml-6">
                  <span
                    className={`text-xs leading-none w-2 text-center ${
                      isActive ? 'text-lime-400' : 'text-transparent'
                    }`}
                    aria-label={isActive ? 'Active version' : undefined}
                    title={isActive ? 'Active version' : undefined}
                  >
                    ●
                  </span>
                  <div className="flex flex-col items-end text-xs">
                    <span className="text-gray-300">
                      CHL <span className="text-white font-medium">{stats.chlCount}</span>
                    </span>
                    <span className="text-gray-300">
                      LMN <span className="text-white font-medium">{stats.lmnCount}</span>
                    </span>
                  </div>
                </div>

                {/* Status */}
                <div className="flex items-center w-[180px] justify-start shrink-0">
                  {renderVersionStatus(progress)}
                </div>

                {/* Always-visible row actions: rename + delete. Duplicate /
                    new version live in the navbar split-button block. */}
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={(e) => handleRenameClick(e, version.id)}
                    title={t('workspace.versionDropdown.rename')}
                    aria-label={t('workspace.versionDropdown.rename')}
                    className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:bg-white/10 hover:text-white transition-colors"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    onClick={(e) => handleDeleteClick(e, version.id)}
                    disabled={versions.length === 1 || isActive}
                    title={t('workspace.versionDropdown.delete')}
                    aria-label={t('workspace.versionDropdown.delete')}
                    className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:bg-white/10 hover:text-red-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
          <div className="bg-[#2a2a2a] border border-[#3a3a3a] rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">
              {t('workspace.versionDropdown.confirmDeleteTitle')}
            </h3>
            <p className="text-gray-300 mb-6">{t('workspace.versionDropdown.confirmDeleteMessage')}</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 text-sm text-gray-300 hover:text-white bg-[#3a3a3a] hover:bg-[#4a4a4a] rounded transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
              >
                {t('workspace.versionDropdown.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {renameModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
          <div className="bg-[#2a2a2a] border border-[#3a3a3a] rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">
              {t('workspace.versionDropdown.renameVersionTitle')}
            </h3>
            <input
              type="text"
              value={newVersionName}
              onChange={(e) => setNewVersionName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRenameConfirm();
                } else if (e.key === 'Escape') {
                  handleRenameCancel();
                }
              }}
              className="w-full px-3 py-2 bg-[#1c1c1c] border border-[#3a3a3a] rounded text-white focus:outline-none focus:border-lime-400 mb-6"
              placeholder={t('workspace.versionDropdown.versionNamePlaceholder')}
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={handleRenameCancel}
                className="px-4 py-2 text-sm text-gray-300 hover:text-white bg-[#3a3a3a] hover:bg-[#4a4a4a] rounded transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleRenameConfirm}
                disabled={!newVersionName.trim()}
                className="px-4 py-2 text-sm text-black bg-lime-400 hover:bg-lime-500 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('workspace.versionDropdown.rename')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
